import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Handshake, Search, Building2, Check, X, Clock, Send, Mail, Phone, MapPin,
  Users, PawPrint, Eye, Bell
} from 'lucide-react';

const PartnershipManager = ({ user, onClose }) => {
  const [activeTab, setActiveTab] = useState('partners');
  const [partnerships, setPartnerships] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [selectedRescue, setSelectedRescue] = useState(null);
  const [requestMessage, setRequestMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [partnerAnimals, setPartnerAnimals] = useState([]);
  const [viewingPartner, setViewingPartner] = useState(null);

  useEffect(() => {
    fetchPartnerships();
  }, []);

  const fetchPartnerships = async () => {
    try {
      const response = await axios.get('/shelter/partnerships');
      setPartnerships(response.data || []);
    } catch (error) {
      console.error('Failed to fetch partnerships');
    }
  };

  const searchRescues = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    setIsSearching(true);
    try {
      const response = await axios.get('/shelter/search-rescues', { params: { query: searchQuery } });
      setSearchResults(response.data?.results || []);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setIsSearching(false);
    }
  };

  const sendPartnershipRequest = async () => {
    if (!selectedRescue) return;
    setLoading(true);
    try {
      await axios.post('/shelter/partnerships/request', {
        partner_id: selectedRescue.user_id,
        message: requestMessage
      });
      toast.success('Partnership request sent!');
      setShowRequestDialog(false);
      setSelectedRescue(null);
      setRequestMessage('');
      setSearchResults([]);
      fetchPartnerships();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  const respondToRequest = async (partnershipId, action) => {
    try {
      await axios.put(`/shelter/partnerships/${partnershipId}/respond`, { action });
      toast.success(action === 'accept' ? 'Partnership accepted!' : 'Partnership declined');
      fetchPartnerships();
    } catch (error) {
      toast.error('Failed to respond');
    }
  };

  const viewPartnerAnimals = async (partner) => {
    setViewingPartner(partner);
    try {
      const response = await axios.get(`/shelter/partner/${partner.user_id}/animals`);
      setPartnerAnimals(response.data || []);
    } catch (error) {
      toast.error('Failed to load partner animals');
    }
  };

  const activePartners = partnerships.filter(p => p.status === 'active');
  const pendingRequests = partnerships.filter(p => p.status === 'pending' && !p.is_requester);
  const sentRequests = partnerships.filter(p => p.status === 'pending' && p.is_requester);

  return (
    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Handshake className="w-5 h-5" />
          Rescue Partnerships
        </DialogTitle>
      </DialogHeader>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="partners">
            Partners ({activePartners.length})
          </TabsTrigger>
          <TabsTrigger value="requests">
            Requests {pendingRequests.length > 0 && <Badge className="ml-2 bg-red-500">{pendingRequests.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="find">Find Rescues</TabsTrigger>
        </TabsList>

        {/* Active Partners */}
        <TabsContent value="partners" className="mt-4">
          {activePartners.length > 0 ? (
            <div className="grid gap-3">
              {activePartners.map((partnership) => (
                <Card key={partnership.partnership_id}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold">
                            {partnership.partner?.organization_name || partnership.partner?.name}
                          </p>
                          <p className="text-sm text-stone-500">{partnership.partner?.email}</p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                            {partnership.partner?.city && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />{partnership.partner.city}
                              </span>
                            )}
                            {partnership.partner?.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />{partnership.partner.phone}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800">Connected</Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewPartnerAnimals(partnership.partner)}
                        >
                          <Eye className="w-4 h-4 mr-1" />View Animals
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="py-8 text-center">
                <Handshake className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No partners yet. Find and connect with other rescues!</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Pending Requests */}
        <TabsContent value="requests" className="mt-4 space-y-4">
          {pendingRequests.length > 0 && (
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Bell className="w-4 h-4 text-orange-500" />
                Incoming Requests
              </h3>
              <div className="space-y-2">
                {pendingRequests.map((req) => (
                  <Card key={req.partnership_id} className="border-orange-200 bg-orange-50">
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{req.rescue_name}</p>
                          <p className="text-sm text-stone-600">{req.message || 'Would like to connect with you'}</p>
                          <p className="text-xs text-stone-500 mt-1">
                            Received {new Date(req.requested_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => respondToRequest(req.partnership_id, 'accept')}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-1" />Accept
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => respondToRequest(req.partnership_id, 'decline')}
                          >
                            <X className="w-4 h-4 mr-1" />Decline
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {sentRequests.length > 0 && (
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                Sent Requests
              </h3>
              <div className="space-y-2">
                {sentRequests.map((req) => (
                  <Card key={req.partnership_id}>
                    <CardContent className="pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{req.partner_name}</p>
                          <p className="text-xs text-stone-500">
                            Sent {new Date(req.requested_at).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge variant="secondary">Pending</Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {pendingRequests.length === 0 && sentRequests.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center">
                <Clock className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <p className="text-stone-500">No pending requests</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Find Rescues */}
        <TabsContent value="find" className="mt-4 space-y-4">
          <div>
            <Label>Search for Rescues</Label>
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Search by name, city, or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchRescues()}
              />
              <Button onClick={searchRescues} disabled={isSearching}>
                <Search className="w-4 h-4 mr-2" />
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </div>

          {searchResults.length > 0 && (
            <div className="space-y-2">
              {searchResults.map((rescue) => (
                <Card key={rescue.user_id}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-stone-100 rounded-full flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-stone-600" />
                        </div>
                        <div>
                          <p className="font-medium">{rescue.organization_name || rescue.name}</p>
                          <p className="text-sm text-stone-500">{rescue.email}</p>
                          {rescue.city && (
                            <p className="text-xs text-stone-400">{rescue.city}, {rescue.state}</p>
                          )}
                        </div>
                      </div>
                      {rescue.is_connected ? (
                        <Badge className="bg-green-100 text-green-800">Connected</Badge>
                      ) : rescue.connection_status === 'pending' ? (
                        <Badge variant="secondary">Pending</Badge>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedRescue(rescue);
                            setShowRequestDialog(true);
                          }}
                        >
                          <Send className="w-4 h-4 mr-1" />Connect
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {searchQuery && searchResults.length === 0 && !isSearching && (
            <p className="text-center text-stone-500 py-4">No rescues found matching "{searchQuery}"</p>
          )}
        </TabsContent>
      </Tabs>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
      </DialogFooter>

      {/* Partnership Request Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Partnership Request</DialogTitle>
          </DialogHeader>
          {selectedRescue && (
            <div className="space-y-4">
              <Card className="bg-stone-50">
                <CardContent className="pt-4">
                  <p className="font-medium">{selectedRescue.organization_name || selectedRescue.name}</p>
                  <p className="text-sm text-stone-500">{selectedRescue.email}</p>
                </CardContent>
              </Card>
              <div>
                <Label>Message (Optional)</Label>
                <Textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Introduce your rescue and why you'd like to connect..."
                  rows={3}
                />
              </div>
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <p className="font-medium">Partnership Benefits:</p>
                <ul className="mt-1 space-y-1 text-blue-700">
                  <li>• Transfer animals directly between rescues</li>
                  <li>• View each other's available animals</li>
                  <li>• Coordinate on rescue efforts</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            <Button onClick={sendPartnershipRequest} disabled={loading}>
              {loading ? 'Sending...' : 'Send Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Partner Animals Dialog */}
      <Dialog open={!!viewingPartner} onOpenChange={() => setViewingPartner(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              {viewingPartner?.organization_name || viewingPartner?.name}'s Animals
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto">
            {partnerAnimals.length > 0 ? (
              <div className="grid grid-cols-2 gap-3">
                {partnerAnimals.map((animal) => (
                  <Card key={animal.animal_id}>
                    <CardContent className="pt-4">
                      <div className="flex items-center gap-3">
                        {animal.photos?.[0] ? (
                          <img src={animal.photos[0].url || animal.photos[0]} alt="" className="w-12 h-12 rounded object-cover" />
                        ) : (
                          <div className="w-12 h-12 bg-stone-100 rounded flex items-center justify-center">
                            <PawPrint className="w-6 h-6 text-stone-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium">{animal.name}</p>
                          <p className="text-xs text-stone-500">{animal.species} • {animal.breed}</p>
                          <Badge variant="outline" className="text-xs mt-1">{animal.status}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <p className="text-center text-stone-500 py-8">No animals available</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewingPartner(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DialogContent>
  );
};

export default PartnershipManager;
